import React from 'react';

import { SelectField } from '../embed.styles';
import {
  CarouselThemeColorGrid,
  CarouselThemeControl,
  CarouselThemeDescription,
  CarouselThemeSelectGrid,
  CarouselThemeSection,
  CarouselThemeTitle,
  ThemeColorInput,
  ThemeColorValue,
  ThemeColorValueRow,
  StyledCheckbox,
} from './embed-carousel-panel.styles';

const COLOR_FIELDS = [
  { key: 'bodyBg', label: 'Body background', defaultColor: '#F7F4EF' },
  { key: 'text', label: 'Text', defaultColor: '#2C3236' },
  { key: 'primary', label: 'Primary', defaultColor: '#00624A' },
  { key: 'buttonText', label: 'Button text', defaultColor: '#FFFFFF' },
  { key: 'surface', label: 'Card surface', defaultColor: '#FFFFFF' },
  { key: 'border', label: 'Card border', defaultColor: '#C8D2DB' },
  { key: 'focus', label: 'Focus', defaultColor: '#0B99E6' },
];

const SPACING_OPTIONS = [
  { key: 'default', label: 'Default', value: '' },
  { key: 'compact', label: 'Compact', value: 'compact' },
  { key: 'normal', label: 'Normal', value: 'normal' },
  { key: 'relaxed', label: 'Relaxed', value: 'relaxed' },
];

const RADIUS_OPTIONS = [
  { key: 'default', label: 'Default', value: '' },
  { key: 'none', label: 'None', value: 'none' },
  { key: 'sm', label: 'Small', value: 'sm' },
  { key: 'md', label: 'Medium', value: 'md' },
  { key: 'lg', label: 'Large', value: 'lg' },
];

export function EmbeddedCarouselThemeControls({ carouselTheme = {}, idPrefix, onCarouselThemeChange }) {
  const handleThemeFieldChange = (key, value) => {
    const nextTheme = { ...carouselTheme };

    if (value) {
      nextTheme[key] = value;
    } else {
      delete nextTheme[key];
    }

    onCarouselThemeChange(nextTheme);
  };

  return (
    <CarouselThemeSection aria-label='Carousel theme'>
      <CarouselThemeTitle>Carousel theme</CarouselThemeTitle>
      <CarouselThemeDescription>
        Optional colors and layout settings apply only to this embedded carousel. Unselected settings keep the default
        menu styling.
      </CarouselThemeDescription>

      <CarouselThemeColorGrid>
        {COLOR_FIELDS.map(({ key, label, defaultColor }) => {
          const selectedColor = carouselTheme[key];

          return (
            <CarouselThemeControl key={key}>
              <StyledCheckbox
                inputId={`${idPrefix}-carousel-theme-${key}`}
                name={`${idPrefix}-carousel-theme-${key}`}
                label={label}
                checked={!!selectedColor}
                onChange={() => handleThemeFieldChange(key, selectedColor ? undefined : defaultColor)}
              />
              {selectedColor && (
                <ThemeColorValueRow>
                  <ThemeColorInput
                    type='color'
                    aria-label={`${label} color`}
                    value={selectedColor}
                    onChange={(event) => handleThemeFieldChange(key, event.target.value.toUpperCase())}
                  />
                  <ThemeColorValue>{selectedColor}</ThemeColorValue>
                </ThemeColorValueRow>
              )}
            </CarouselThemeControl>
          );
        })}
      </CarouselThemeColorGrid>

      <CarouselThemeSelectGrid>
        <SelectField
          id={`${idPrefix}-carousel-theme-spacing`}
          title='Spacing'
          data-testid={`${idPrefix}-carousel-theme-spacing`}
          options={SPACING_OPTIONS}
          value={carouselTheme.spacing ?? ''}
          onChange={(event) => handleThemeFieldChange('spacing', event.target.value)}
        />
        <SelectField
          id={`${idPrefix}-carousel-theme-radius`}
          title='Corner radius'
          data-testid={`${idPrefix}-carousel-theme-radius`}
          options={RADIUS_OPTIONS}
          value={carouselTheme.radius ?? ''}
          onChange={(event) => handleThemeFieldChange('radius', event.target.value)}
        />
      </CarouselThemeSelectGrid>
    </CarouselThemeSection>
  );
}
